import React, { useState, useRef, useEffect } from "react";
import { ClickAwayListener } from "@mui/material";
import {
  AppBar, Toolbar, Typography, Box, IconButton,
  Tooltip, MenuItem, Menu, Avatar, Badge, Backdrop,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { hasPermission } from "../utils/auth";
import { useCurrentUser } from "../utils/useCurrentUser";
import { useFavoritesContext } from "../utils/FavoritesContext";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import HomeIcon from "@mui/icons-material/HomeOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import GroupIcon from "@mui/icons-material/Group";
import BadgeIcon from "@mui/icons-material/Badge";
import DevicesOtherOutlinedIcon from "@mui/icons-material/DevicesOtherOutlined";
import DevicesIcon from "@mui/icons-material/Devices";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import SecurityIcon from "@mui/icons-material/Security";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import CorporateFareOutlinedIcon from "@mui/icons-material/CorporateFareOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";

const APPBAR_HEIGHT = 64;
const SIDEBAR_WIDTH = 100;
const ICON_PIXEL_SIZE = 26;

const Sidebar = ({ mobileOpen, onToggle, isMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { empId, boId, userId } = useCurrentUser();
  const { isFavorite, toggleFavorite } = useFavoritesContext();
  const [openSubmenu, setOpenSubmenu] = useState("");
  const submenuRef = useRef(null);
  const [submenuPosition, setSubmenuPosition] = useState({ top: 100, left: 100 });
  const [anchorEl, setAnchorEl] = useState(null);
  const openAccountMenu = Boolean(anchorEl);
  const [userDetails, setUserDetails] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (!empId || !boId) return;
    const fetchUserDetails = async () => {
      try {
        const res = await fetch(`${BASE_URL}/EmployeeDetails/${boId}/${empId}`);
        if (res.ok) {
          const data = await res.json();
          setUserDetails(data);
          const picRes = await fetch(`${BASE_URL}/api/users/${empId}/profile-picture?bo_id=${boId}`);
          if (picRes.ok) {
            const picData = await picRes.json();
            if (picData.success && picData.data?.profile_picture_url) {
              setProfilePicture(picData.data.profile_picture_url);
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch user details:", err);
      }
    };
    fetchUserDetails();

    // Listen for profile picture changes from Profile page (same browser tab or other tabs)
    const channel = new BroadcastChannel("profile_picture");
    channel.onmessage = (e) => {
      if (e.data.type === "updated") {
        setProfilePicture(e.data.url);
      } else if (e.data.type === "deleted") {
        setProfilePicture(null);
      }
    };
    return () => channel.close();
  }, [empId, boId, BASE_URL]);

  const handleAccountClick = (e) => setAnchorEl(e.currentTarget);
  const handleAccountClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    try {
      await fetch(`${BASE_URL}/api/logout`, { method: "POST", credentials: "include" });
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      window.location.href = "/login";
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    setOpenSubmenu("");
    if (isMobile) onToggle();
  };

  const renderIcon = (icon) => {
    if (!React.isValidElement(icon)) return icon;
    return React.cloneElement(icon, {
      style: { fontSize: ICON_PIXEL_SIZE, display: "block", lineHeight: 1 },
    });
  };

  const mainMenus = [
    { label: "Dashboard", icon: <HomeIcon />, path: "/dashboard", permission: { screen: "Dashboard", action: "View" } },
    { label: "Organization", icon: <CorporateFareOutlinedIcon />, path: "/organization", permission: { screen: "Organization", action: "View" } },
    { label: "Department", icon: <HomeIcon />, path: "/Department", permission: { screen: "Department", action: "View" } },
    {
      label: "Clients", icon: <BusinessOutlinedIcon />, hoverIcon: <GroupIcon />, path: "/Clients",
      permission: { screen: "Clients", action: "View" },
      submenus: [
        { label: "Clients", path: "/clients", icon: <BusinessOutlinedIcon />, permission: { screen: "Clients", action: "View" } },
        { label: "Client Projects", path: "/clientprojects", icon: <FolderOpenOutlinedIcon />, permission: { screen: "Client Projects", action: "View" } },
      ],
    },
    { label: "Assets", icon: <DevicesOtherOutlinedIcon />, hoverIcon: <DevicesIcon />, path: "/assets", permission: { screen: "Assets", action: "View" } },
    {
      label: "Employees", icon: <PeopleAltOutlinedIcon />, hoverIcon: <BadgeIcon />, path: "/employees",
      permission: { screen: "Employees", action: "View" },
      submenus: [
        { label: "Employee", path: "/employeedetails", icon: <PeopleAltOutlinedIcon />, permission: { screen: "Employees", action: "View" } },
        { label: "Employee Projects", path: "/employeeprojects", icon: <WorkOutlineIcon />, permission: { screen: "Employee Projects", action: "View" } },
        { label: "Timesheet", path: "/timesheet", icon: <AccessTimeOutlinedIcon />, permission: { screen: "Timesheet", action: "View" } },
        { label: "Timesheet Tasks", path: "/timesheettracker", icon: <FactCheckOutlinedIcon />, permission: { screen: "Timesheet Approval", action: "View" } },
        { label: "Payslip", path: "/payslip", icon: <FolderSharedOutlinedIcon />, permission: { screen: "Payslip", action: "View" } },
        { label: "Leave Request", path: "/leaverequest", icon: <EventNoteOutlinedIcon />, permission: { screen: "Leave Request", action: "View" } },
        { label: "Leave Approval", path: "/leaveapproval", icon: <AssignmentTurnedInOutlinedIcon />, permission: { screen: "Leave Approval", action: "View" } },
        { label: "Employee Salaries", path: "/employeesalaries", icon: <FolderSharedOutlinedIcon />, permission: { screen: "Employee Salaries", action: "View" } },
        { label: "Employee Assets", path: "/employeeassets", icon: <Inventory2OutlinedIcon />, permission: { screen: "Employee Assets", action: "View" } },
        { label: "Employee Attendance", path: "/employeeattendance", icon: <EventAvailableOutlinedIcon /> },
        { label: "Exp & Skills", path: "/experience-skills", icon: <StarOutlineIcon /> },
        { label: "Holidays", path: "/holidays", icon: <CelebrationOutlinedIcon /> },
        { label: "Allowance", path: "/employee-allowance", icon: <FolderSharedOutlinedIcon /> },
         {
          label: "Anti Virus",
          path: "/antivirus",
          icon: <SecurityIcon />,
      },
      ],
    },
    {
      label: "User", icon: <PersonOutlineIcon />, path: "/user",
      permission: { screen: "User", action: "View" },
      submenus: [
        { label: "Users", path: "/users", icon: <ManageAccountsOutlinedIcon />, permission: { screen: "Users", action: "View" } },
        { label: "Roles", path: "/roles", icon: <SecurityIcon />, permission: { screen: "Roles", action: "View" } },
        { label: "User Roles", path: "/userroles", icon: <BadgeIcon />, permission: { screen: "User Roles", action: "View" } },
        { label: "Screens", path: "/screens", icon: <DashboardOutlinedIcon />, permission: { screen: "Screens", action: "View" } },
        { label: "Groups", path: "/groups", icon: <GroupsOutlinedIcon />, permission: { screen: "Groups", action: "View" } },
        { label: "Permissions", path: "/permissions", icon: <VpnKeyIcon />, permission: { screen: "Permissions", action: "View" } },
      ],
    },
  ];

  const filteredMenus = mainMenus.map((menu) => {
    const filteredSubmenus = menu.submenus?.filter((sub) =>
      !sub.permission || hasPermission(sub.permission.screen, sub.permission.action)
    ) || [];
    const hasMenuPermission = !menu.permission || hasPermission(menu.permission.screen, menu.permission.action);
    if (menu.submenus) return filteredSubmenus.length > 0 ? { ...menu, submenus: filteredSubmenus } : null;
    return hasMenuPermission ? menu : null;
  }).filter(Boolean);

  const submenuItems = filteredMenus.find((m) => m.label === openSubmenu)?.submenus || [];

  // Clamp submenu position so it never overflows the viewport
  const clampedTop = Math.min(
    submenuPosition.top,
    window.innerHeight - Math.ceil(submenuItems.length / 3) * 100 - 20
  );

  const avatarInitials = !profilePicture && userDetails
    ? `${userDetails.first_name?.[0] ?? ""}${userDetails.last_name?.[0] ?? ""}`.toUpperCase()
    : "U";

  const avatarSrc = profilePicture ? profilePicture : undefined;

  return (
    <>
      {/* ── Top AppBar ── */}
      <AppBar position="fixed" elevation={0} sx={{ backgroundColor: "#fff", color: "#333", zIndex: 1300, borderBottom: "1px solid #eee" }}>
        <Toolbar sx={{ minHeight: APPBAR_HEIGHT, height: APPBAR_HEIGHT }}>
          {/* Animated hamburger / close */}
          <IconButton
            color="inherit"
            edge="start"
            onClick={onToggle}
            sx={{
              mr: 1.5,
              transition: "transform 0.3s ease",
              transform: mobileOpen ? "rotate(90deg)" : "rotate(0deg)",
            }}
          >
            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>

          <Box component="img" src={`${process.env.PUBLIC_URL}/BloomPeople.svg`} alt="Logo" sx={{ height: 52, width: "auto", display: "block" }} />
          <Box flexGrow={1} />

          <Tooltip title="Account">
            <IconButton onClick={handleAccountClick} sx={{ p: 0.5 }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                badgeContent={<Box sx={{ width: 11, height: 11, borderRadius: "50%", bgcolor: "#4caf50", border: "2px solid white" }} />}
              >
                <Avatar
                  src={avatarSrc}
                  sx={{
                    width: 38, height: 38,
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    border: "2px solid #e0e0e0",
                    fontSize: 15, fontWeight: 600,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                  }}
                >
                  {avatarInitials}
                </Avatar>
              </Badge>
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* ── Profile Menu ── */}
      <Menu
        anchorEl={anchorEl}
        open={openAccountMenu}
        onClose={handleAccountClose}
        onClick={handleAccountClose}
        PaperProps={{
          elevation: 8,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 12px rgba(0,0,0,0.12))",
            mt: 1.5, minWidth: 220, borderRadius: 2,
            "&:before": {
              content: '""', display: "block", position: "absolute",
              top: 0, right: 14, width: 10, height: 10,
              bgcolor: "background.paper", transform: "translateY(-50%) rotate(45deg)", zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #f0f0f0" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar src={avatarSrc} sx={{ width: 38, height: 38, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
              {avatarInitials}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {userDetails ? `${userDetails.first_name} ${userDetails.last_name}` : "User"}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                {userDetails?.bfx_email || ""}
              </Typography>
            </Box>
          </Box>
        </Box>
        <MenuItem onClick={() => { handleAccountClose(); navigate("/profile"); }} sx={{ py: 1, px: 2, "&:hover": { bgcolor: "rgba(102,126,234,0.08)" } }}>
          <PersonOutlineIcon sx={{ mr: 1.5, fontSize: 20, color: "#667eea" }} />
          <Typography variant="body2">Profile</Typography>
        </MenuItem>
        <MenuItem onClick={() => { handleAccountClose(); handleLogout(); }} sx={{ py: 1, px: 2, color: "#f44336", "&:hover": { bgcolor: "rgba(244,67,54,0.08)" } }}>
          <LogoutIcon sx={{ mr: 1.5, fontSize: 20 }} />
          <Typography variant="body2">Logout</Typography>
        </MenuItem>
      </Menu>

      {/* ── Mobile backdrop ── */}
      {isMobile && (
        <Backdrop
          open={mobileOpen}
          onClick={onToggle}
          sx={{ zIndex: 1199, backdropFilter: "blur(2px)", backgroundColor: "rgba(0,0,0,0.3)" }}
        />
      )}

      {/* ── Sidebar panel ── */}
      <Box
        sx={{
          position: "fixed",
          top: APPBAR_HEIGHT,
          left: 0,
          height: `calc(100vh - ${APPBAR_HEIGHT}px)`,
          width: SIDEBAR_WIDTH,
          backgroundColor: "#fafafa",
          borderRight: "1px solid #eee",
          display: "flex",
          flexDirection: "column",
          pt: 1,
          zIndex: 1200,
          overflowY: "auto",
          overflowX: "hidden",
          // smooth slide
          transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: mobileOpen ? "4px 0 20px rgba(0,0,0,0.08)" : "none",
        }}
      >
        {filteredMenus.map((menu) => {
          const isActive =
            openSubmenu === menu.label ||
            location.pathname === menu.path ||
            (menu.submenus && menu.submenus.some((sub) => location.pathname.startsWith(sub.path)));
          const displayedIcon = isActive && menu.hoverIcon ? menu.hoverIcon : menu.icon;

          return (
            <Tooltip title={menu.label} placement="right" key={menu.label}>
              <Box
                onClick={(e) => {
                  if (menu.submenus) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setOpenSubmenu((prev) => prev === menu.label ? "" : menu.label);
                    setSubmenuPosition({ top: rect.top + rect.height / 2 - 75, left: rect.right + 10 });
                  } else {
                    setOpenSubmenu("");
                    handleNavigation(menu.path);
                  }
                }}
                sx={{
                  display: "flex", flexDirection: "column", alignItems: "center",
                  cursor: "pointer", padding: 1, mx: 0.5, borderRadius: 2,
                  color: isActive ? "#1976d2" : "#555",
                  backgroundColor: isActive ? "#e3f2fd" : "transparent",
                  transition: "all 0.2s ease",
                  height: 72, minHeight: 72, boxSizing: "border-box",
                  "&:hover": {
                    backgroundColor: isActive ? "#e3f2fd" : "#f0f4ff",
                    color: "#1976d2",
                    transform: "scale(1.04)",
                  },
                }}
              >
                <Box sx={{ lineHeight: 1 }}>{renderIcon(displayedIcon)}</Box>
                <Typography variant="caption" sx={{ fontSize: 11, lineHeight: 1.1, mt: 0.5, textAlign: "center", whiteSpace: "nowrap" }}>
                  {menu.label}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* ── Floating submenu ── */}
      {openSubmenu && (
        <ClickAwayListener onClickAway={() => setOpenSubmenu("")}>
          <Box
            ref={submenuRef}
            sx={{
              position: "fixed",
              top: Math.max(APPBAR_HEIGHT + 8, clampedTop),
              left: submenuPosition.left,
              backgroundColor: "#fff",
              border: "1px solid #e8e8e8",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: "14px",
              padding: "8px",
              display: "grid",
              gridTemplateColumns: `repeat(${Math.min(3, submenuItems.length)}, 72px)`,
              gap: "6px",
              zIndex: 1400,
              maxHeight: `calc(100vh - ${APPBAR_HEIGHT + 16}px)`,
              overflowY: "auto",
              animation: "fadeSlideIn 0.18s ease",
              "@keyframes fadeSlideIn": {
                from: { opacity: 0, transform: "translateX(-8px) scale(0.97)" },
                to: { opacity: 1, transform: "translateX(0) scale(1)" },
              },
            }}
          >
            {submenuItems.map((submenu) => (
              <Box
                key={submenu.label}
                sx={{
                  display: "flex", flexDirection: "column", alignItems: "center",
                  justifyContent: "center", cursor: "pointer", padding: "8px 4px",
                  borderRadius: 2, textAlign: "center",
                  transition: "all 0.15s ease",
                  "&:hover": { backgroundColor: "#f0f8ff", transform: "translateY(-2px)" },
                  minWidth: 72,
                  position: "relative",
                }}
              >
                {/* Star toggle */}
                <Tooltip title={isFavorite(submenu.path) ? "Remove from favourites" : "Add to favourites"} placement="top">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); toggleFavorite(submenu.path, submenu.label); }}
                    sx={{
                      position: "absolute", top: 2, right: 2,
                      padding: "2px",
                      color: isFavorite(submenu.path) ? "#f59e0b" : "#504b4bff",
                      "&:hover": { color: "#f59e0b", backgroundColor: "transparent" },
                    }}
                  >
                    {isFavorite(submenu.path)
                      ? <StarIcon sx={{ fontSize: 13 }} />
                      : <StarBorderIcon sx={{ fontSize: 13 }} />}
                  </IconButton>
                </Tooltip>

                <Box
                  onClick={() => handleNavigation(submenu.path)}
                  sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 34 }}>
                    {React.cloneElement(submenu.icon, { fontSize: "medium" })}
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: 11, lineHeight: 1.2, mt: 0.5, textAlign: "center" }}>
                    {submenu.label}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </ClickAwayListener>
      )}
    </>
  );
};

export default Sidebar;
